import { useState, useMemo } from "react";

/**
 * Evening note.
 *
 * This used to be a pulsing red countdown built on loss aversion — a timer, an
 * "urgency" escalation and a warning buzz. It was then softened in wording, but
 * the mechanism stayed: it led with the streak numeral as its headline and put
 * a food prompt next to it. Both are gone.
 *
 * What is left is an invitation. The heading is the offer, not a count. If the
 * number appears at all it is a quiet aside in the body line, phrased as
 * something already done rather than something about to be lost. And the two
 * actions are of equal weight, because resting is a real answer — the copy has
 * always said so and now the UI has a button for it.
 *
 * It renders below the check-in, never above it: nothing precedes how the
 * person is doing today. The visibility rules are unchanged.
 */
export default function EveningNote({
  currentStreak,
  hasActivityToday,
  onQuickAction,
}) {
  const [dismissed, setDismissed] = useState(false);

  const isEvening = useMemo(() => {
    const hour = new Date().getHours();
    return hour >= 18; // 6 PM or later
  }, []);

  const isAtRisk = currentStreak > 0 && !hasActivityToday && isEvening;

  if (!isAtRisk || dismissed) return null;

  const history =
    currentStreak === 1
      ? "You showed up yesterday."
      : `You've shown up ${currentStreak} days running.`;

  return (
    <section className="shell-nudge" aria-label="Evening note">
      <div className="shell-nudge-head">
        <span className="shell-nudge-mark" aria-hidden="true">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.5 14.2A8.5 8.5 0 1 1 9.8 3.5a6.8 6.8 0 0 0 10.7 10.7z" />
          </svg>
        </span>

        <div className="shell-nudge-text">
          <h2 className="shell-nudge-title">
            There&rsquo;s still time for something small.
          </h2>
          <p className="shell-nudge-body">
            {history} Ten minutes still counts, and so does calling it a rest
            night — either one is a fine way to end the day.
          </p>
        </div>

        <button
          type="button"
          className="shell-nudge-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Hide this for now"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="shell-nudge-actions">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => onQuickAction?.("workout")}
        >
          Something small
        </button>
        <button
          type="button"
          className="btn btn-quiet btn-sm"
          onClick={() => setDismissed(true)}
        >
          Resting tonight
        </button>
      </div>
    </section>
  );
}
